// Columna de filtros de Inventario v3: Búsqueda · Tipo · Gama · Tienda del tenant.
import type { ReactNode } from 'react';
import { FiltersColumn } from '@/shared/patterns/FiltersColumn';
import { cn } from '@/lib/cn';
import type { Store } from '@/api/services/retail-org/retail-org.client';
import { KIND_OPTIONS, TIER_OPTIONS } from '../lib/inventoryFormat';
import { activeFilterCount, toggleValue, type InventoryFilters } from '../lib/inventoryList';

const LABEL = 'mb-[5px] text-[9.5px] font-bold uppercase tracking-[0.08em] text-muted-foreground';
const CHIP =
    'cursor-pointer whitespace-nowrap rounded-full border px-1.5 py-0.5 text-[9.5px] font-semibold transition-colors';
const FIELD =
    'w-full rounded border border-border bg-card px-2 text-[11.5px] text-foreground focus:border-ring focus:outline-none';

function Group({ label, children }: { label: string; children: ReactNode }) {
    return (
        <div className="mt-2.5">
            <div className={LABEL}>{label}</div>
            {children}
        </div>
    );
}

function Chip({
    on,
    onClick,
    children,
}: {
    on: boolean;
    onClick: () => void;
    children: ReactNode;
}) {
    return (
        <button
            type="button"
            aria-pressed={on}
            onClick={onClick}
            className={cn(
                CHIP,
                on
                    ? 'border-primary bg-primary text-primary-foreground'
                    : 'border-border bg-card hover:bg-accent',
            )}
        >
            {children}
        </button>
    );
}

export function InventoryFiltersPanel({
    filters,
    onChange,
    storeId,
    onStoreChange,
    stores,
    onReset,
    collapsed,
    onToggleCollapsed,
}: {
    filters: InventoryFilters;
    onChange: (next: InventoryFilters) => void;
    storeId: string;
    onStoreChange: (storeId: string) => void;
    stores: readonly Store[];
    onReset: () => void;
    /** Plegado, el panel queda en un riel de 40px con el botón para expandirlo. */
    collapsed: boolean;
    onToggleCollapsed: () => void;
}) {
    return (
        <FiltersColumn
            collapsed={collapsed}
            onToggleCollapsed={onToggleCollapsed}
            activeCount={activeFilterCount(filters, storeId)}
            onReset={onReset}
        >
            <Group label="Búsqueda">
                <input
                    type="text"
                    value={filters.query}
                    onChange={(e) => {
                        onChange({ ...filters, query: e.target.value });
                    }}
                    placeholder="SKU, marca, modelo"
                    aria-label="Búsqueda"
                    className={cn(FIELD, 'bg-background py-1')}
                />
            </Group>

            <Group label="Tipo">
                <div className="flex flex-wrap gap-[3px]">
                    {KIND_OPTIONS.map((option) => (
                        <Chip
                            key={option.value}
                            on={filters.kinds.includes(option.value)}
                            onClick={() => {
                                onChange({
                                    ...filters,
                                    kinds: toggleValue(filters.kinds, option.value),
                                });
                            }}
                        >
                            {option.label}
                        </Chip>
                    ))}
                </div>
            </Group>

            <Group label="Gama">
                <div className="flex flex-wrap gap-[3px]">
                    {TIER_OPTIONS.map((option) => (
                        <Chip
                            key={option.value}
                            on={filters.tiers.includes(option.value)}
                            onClick={() => {
                                onChange({
                                    ...filters,
                                    tiers: toggleValue(filters.tiers, option.value),
                                });
                            }}
                        >
                            {option.label}
                        </Chip>
                    ))}
                </div>
            </Group>

            <Group label="Tienda del tenant">
                <select
                    value={storeId}
                    onChange={(e) => {
                        onStoreChange(e.target.value);
                    }}
                    aria-label="Tienda del tenant"
                    className={cn(FIELD, 'h-[26px] cursor-pointer')}
                >
                    <option value="">Todas (consolidado)</option>
                    {stores.map((store) => (
                        <option key={store.storeId} value={store.storeId}>
                            {`${store.code} · ${store.name}`}
                        </option>
                    ))}
                </select>
                <div className="mt-1 text-[10px] italic text-muted-foreground">
                    Al elegir una, el stock consolidado colapsa a esa tienda.
                </div>
            </Group>
        </FiltersColumn>
    );
}
